import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Form, Icon, Utils } from 'app/main/components';
import { useModel, useSettings, useUserData } from 'app/main/components/hooks';
import {
	CurrencyTextField,
	DateField,
	ModuleSearchField,
	NumberTextField,
	SearchSelectField,
	SelectField,
	SimpleTextField
} from 'app/main/components/fields';

interface OrdersBillingEditDialogProps {
	baseModel: any;
	isAdd?: boolean;
	updateRefresh: VoidFunction;
}

export function showOrdersBillingEditDialog(props: OrdersBillingEditDialogProps) {
	return openDialog({
		children: <OrdersBillingEditDialog {...props} />,
		maxWidth: 'sm',
		fullWidth: true
	});
}

export default function OrdersBillingEditDialog(props: OrdersBillingEditDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('orders_billing');
	const userData = useUserData();

	const { model, modifiedModel, sendChange, save } = useModel({
		baseModel: props.baseModel,
		settings,
		isEdit: true,
		isAdd: props.isAdd
	});

	function saveModel() {
		if (!props.isAdd) {
			save({
				onSuccess: () => {
					dispatch(Utils.alertSuccessMsg(t));
					dispatch(closeDialog());
					props.updateRefresh();
				}
			});
		} else {
			dispatch(Utils.alertProgressMsg(t));
			Utils.executeStored({
				action: 'st_orders_billing_add',
				dispatch,
				data: {
					orbi_ords_uid: model.orbi_ords_uid,
					orbi_user_created_uid: userData.user_uid,
					billing: [modifiedModel]
				},
				successHandler: () => {
					dispatch(Utils.alertSuccessMsg(t));
					dispatch(closeDialog());
					props.updateRefresh();
				}
			});
		}
	}

	const amountAvailable =
		model.inty_group === 'expenses' ? model.orbi_expenses_available : model.orbi_price_available;

	return (
		<>
			<DialogTitle>
				{t(props.isAdd ? 'DIALOG_ORDERS_BILLING_DIALOG_ADD_TITLE' : 'DIALOG_ORDERS_BILLING_DIALOG_EDIT_TITLE')}
			</DialogTitle>
			<DialogContent>
				<Form id="ordersBillingEditDialog" onSubmit={saveModel}>
					<Grid container spacing={1}>
						{/* DESCRIPTION */}
						<Grid size={12}>
							<SimpleTextField
								name="orbi_description"
								label={t('DETAILS_ORDERS_BILLING_FIELD_DESCRIPTION')}
								value={model.orbi_description}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						{/* INVOICE TYPE */}
						<Grid size={{ xs: 12 }}>
							<SearchSelectField
								name="orbi_inty_id"
								label={t('DETAILS_ORDERS_BILLING_FIELD_INVOICE_TYPE')}
								systable="invoices_types"
								value={model.orbi_inty_id}
								isEdit
								sendChange={sendChange}
								afterChange={newValue => {
									sendChange('inty_group', newValue.inty_group);
									sendChange('orbi_orli_uid', null);
								}}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						{/* ORDER LINE */}
						<Grid size={{ xs: 12 }}>
							<ModuleSearchField
								name="orbi_orli_uid"
								label={t('DETAILS_ORDERS_BILLING_FIELD_ORDER_LINE')}
								module="orders_lines"
								uid={model.orbi_orli_uid}
								filters={[
									{
										filt_field: 'orli_ords_uid',
										filt_clause: 'equals',
										filt_value: model.orbi_ords_uid
									},
									{
										filt_field: 'orli_type',
										filt_clause: 'in',
										filt_value: ['item', 'workreports']
									},
									{
										filt_field:
											model.inty_group !== 'incomes'
												? 'orli_tot_expenses'
												: 'orli_tot_value_tax_excl',
										filt_clause: 'greater',
										filt_value: 0
									}
								]}
								isEdit={!!model.orbi_inty_id}
								sendChange={sendChange}
								afterChange={newValue => {
									sendChange('orli_quantity', newValue?.orli_quantity);
									sendChange('orli_tot_value_tax_excl', newValue?.orli_tot_value_tax_excl);
								}}
							/>
						</Grid>
						{/* COMPANY */}
						<Grid size={12}>
							<ModuleSearchField
								name="orbi_comp_uid"
								label={t('DETAILS_ORDERS_BILLING_FIELD_COMPANY')}
								module="companies"
								uid={model.orbi_comp_uid}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* TYPE */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<SelectField
								name="orbi_type"
								label={t('DETAILS_ORDERS_BILLING_FIELD_TYPE')}
								options={[
									{ value: 'quantity', title: t('DETAILS_ORDERS_BILLING_FIELD_TYPE_QUANTITY') },
									{ value: 'percentage', title: t('DETAILS_ORDERS_BILLING_FIELD_TYPE_PERCENTAGE') },
									{ value: 'amount', title: t('DETAILS_ORDERS_BILLING_FIELD_TYPE_AMOUNT') }
								]}
								value={model.orbi_type}
								isEdit
								sendChange={sendChange}
								afterChange={() => {
									sendChange('orbi_quantity', null);
									sendChange('orbi_percentage', null);
									sendChange('orbi_amount', null);
								}}
								disableEmptyOption
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							{/* QUANTITY */}
							{model.orbi_type === 'quantity' && (
								<NumberTextField
									name="orbi_quantity"
									label={t('DETAILS_ORDERS_BILLING_FIELD_QUANTITY')}
									value={model.orbi_quantity}
									isEdit
									sendChange={sendChange}
									required
									validations={{
										minValue: 0,
										maxValue: model.orli_quantity
									}}
									validationErrors={{
										isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
										minValue: t('ERROR_MIN_VALUE', { value: 0 }),
										maxValue: t('ERROR_MAX_VALUE', { value: model.orli_quantity })
									}}
								/>
							)}
							{/* PERCENTAGE */}
							{model.orbi_type === 'percentage' && (
								<NumberTextField
									name="orbi_percentage"
									label={t('DETAILS_ORDERS_BILLING_FIELD_PERCENTAGE')}
									value={model.orbi_percentage}
									endAdornment={<Icon icon="percent-outline" size={20} />}
									isEdit
									sendChange={sendChange}
									required
									validations={{
										minValue: 0,
										maxValue: 100
									}}
									validationErrors={{
										isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
										minValue: t('ERROR_MIN_VALUE', { value: 0 }),
										maxValue: t('ERROR_MAX_VALUE', { value: 100 })
									}}
								/>
							)}
							{/* AMOUNT */}
							{model.orbi_type === 'amount' && (
								<CurrencyTextField
									name="orbi_amount"
									label={t('DETAILS_ORDERS_BILLING_FIELD_AMOUNT')}
									value={model.orbi_amount}
									isEdit
									sendChange={sendChange}
									required
									validations={{
										minValue: 0,
										maxValue: amountAvailable
									}}
									validationErrors={{
										isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
										minValue: t('ERROR_MIN_CURRENCY', {
											value: 0,
											formatParams: { value: { currency: 'EUR', locale: 'it' } }
										}),
										maxValue: t('ERROR_MAX_CURRENCY', {
											value: amountAvailable,
											formatParams: { value: { currency: 'EUR', locale: 'it' } }
										})
									}}
								/>
							)}
						</Grid>
						{/* BILLING TYPE */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<SelectField
								name="orbi_schedule"
								label={t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE')}
								options={[
									{ value: 'sign', title: t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE_SIGNED') },
									{ value: 'date', title: t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE_DATE') },
									{ value: 'milestone', title: t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE_MILESTONE') }
								]}
								value={model.orbi_schedule}
								isEdit
								sendChange={sendChange}
								afterChange={() => {
									sendChange('orbi_d_schedule', null);
									sendChange('orbi_prst_id', null);
								}}
								disableEmptyOption
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							{/* BILLING DATE */}
							{model.orbi_schedule === 'date' && (
								<DateField
									name="orbi_d_schedule"
									label={t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE_DATE')}
									value={model.orbi_d_schedule}
									isEdit
									sendChange={sendChange}
									required
									validations={{
										minDate: Utils.parseDate(model.ords_d_sign)
									}}
									validationErrors={{
										isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED')
									}}
								/>
							)}
							{/* PHASE */}
							{model.orbi_schedule === 'milestone' && (
								<SearchSelectField
									name="orbi_prst_id"
									label={t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE_MILESTONE')}
									systable="projects_statuses"
									value={model.orbi_prst_id}
									isEdit
									sendChange={sendChange}
									required
									validationError={t('ERROR_TEXT_REQUIRED')}
								/>
							)}
						</Grid>
						{/* PAYMENT TERMS */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<SearchSelectField
								name="orbi_pate_id"
								label={t('DETAILS_ORDERS_BILLING_FIELD_PAYMENTTERMS')}
								systable="paymentterms"
								value={model.orbi_pate_id}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* PAYMENT METHOD */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<SearchSelectField
								name="orbi_pame_id"
								label={t('DETAILS_ORDERS_BILLING_FIELD_PAYMENTMETHOD')}
								systable="paymentmethods"
								value={model.orbi_pame_id}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="ordersBillingEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
